/**
 * ImageUploadField — upload a day image to Firebase Storage with live preview.
 *
 * Shows a local blob preview instantly on file select, then swaps to the
 * Firebase URL once the upload completes. Calls onUploadComplete / onDelete
 * so the parent can persist to Firestore immediately.
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import { ImageIcon, Upload, Trash2, X, ZoomIn } from 'lucide-react';
import { cn } from '@/utils/cn';
import { uploadImage, deleteMedia } from '@/services/mediaService';
import {
  IMAGE_ACCEPT_STRING,
  MAX_IMAGE_SIZE_BYTES,
  formatFileSize,
  validateImageFile,
} from '@/utils/fileUtils';
import type { Audience } from '@/types/auth.types';
import type { UploadProgress } from '@/services/mediaService';

interface ImageUploadFieldProps {
  audience:  Audience;
  dayNumber: number;
  /** Current persisted image URL (from Firestore) */
  imageUrl:         string | null;
  /** Current persisted storage path (from Firestore) */
  imageStoragePath: string | null;
  onUploadComplete: (url: string, storagePath: string) => void;
  onDelete:         () => void;
  disabled?:        boolean;
}

export default function ImageUploadField({
  audience,
  dayNumber,
  imageUrl,
  imageStoragePath,
  onUploadComplete,
  onDelete,
  disabled,
}: ImageUploadFieldProps) {
  const inputRef   = useRef<HTMLInputElement>(null);

  const [progress,  setProgress]  = useState<UploadProgress | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [deleting,  setDeleting]  = useState(false);
  const [localUrl,  setLocalUrl]  = useState<string | null>(null);
  const [lightbox,  setLightbox]  = useState(false);
  const cancelRef  = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => { if (localUrl) URL.revokeObjectURL(localUrl); };
  }, [localUrl]);

  const handleFileSelect = useCallback((file: File) => {
    setError(null);
    const v = validateImageFile(file);
    if (!v.valid) { setError(v.error); return; }

    const blob = URL.createObjectURL(file);
    setLocalUrl(blob);
    setUploading(true);
    setProgress({ bytesTransferred: 0, totalBytes: file.size, percentage: 0 });

    const cancel = uploadImage(
      audience,
      dayNumber,
      file,
      p => setProgress(p),
      ({ url, storagePath }) => {
        setUploading(false);
        setProgress(null);
        onUploadComplete(url, storagePath);
      },
      err => {
        setUploading(false);
        setProgress(null);
        if (localUrl) URL.revokeObjectURL(localUrl);
        setLocalUrl(null);
        setError(err.message);
      },
    );
    cancelRef.current = cancel;
  }, [audience, dayNumber, onUploadComplete, localUrl]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleCancelUpload = () => {
    cancelRef.current?.();
    setUploading(false);
    setProgress(null);
    if (localUrl) URL.revokeObjectURL(localUrl);
    setLocalUrl(null);
  };

  const handleDelete = async () => {
    if (!imageStoragePath) { onDelete(); return; }
    setDeleting(true);
    try {
      await deleteMedia(imageStoragePath);
      if (localUrl) { URL.revokeObjectURL(localUrl); setLocalUrl(null); }
      onDelete();
    } catch {
      setError('Failed to delete image. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const previewUrl = localUrl ?? imageUrl;

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-surface-700">Day Image</label>

      {/* Image preview */}
      {previewUrl && (
        <div className="group relative rounded-xl overflow-hidden border border-surface-200 bg-surface-50">
          <img
            src={previewUrl}
            alt="Day image"
            className="w-full max-h-64 object-cover"
          />

          {/* Overlay actions */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors
                          flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100">
            <button
              type="button"
              onClick={() => setLightbox(true)}
              className="flex h-9 w-9 items-center justify-center rounded-full
                         bg-white/90 text-surface-700 hover:bg-white shadow transition-all"
              title="View full size"
            >
              <ZoomIn className="h-4 w-4" />
            </button>
            {!disabled && !uploading && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="flex h-9 w-9 items-center justify-center rounded-full
                           bg-red-500/90 text-white hover:bg-red-600 shadow transition-all
                           disabled:opacity-50"
                title="Remove image"
              >
                {deleting
                  ? <span className="h-4 w-4 animate-spin border-2 border-white border-t-transparent rounded-full" />
                  : <Trash2 className="h-4 w-4" />
                }
              </button>
            )}
          </div>

          {/* Upload overlay progress */}
          {uploading && progress && (
            <div className="absolute bottom-0 inset-x-0 bg-black/60 px-4 py-3 space-y-1.5">
              <div className="flex items-center justify-between text-xs text-white">
                <span>Uploading {progress.percentage}%</span>
                <button type="button" onClick={handleCancelUpload}
                  className="flex items-center gap-1 text-red-300 hover:text-red-100"
                >
                  <X className="h-3 w-3" /> Cancel
                </button>
              </div>
              <div className="h-1.5 rounded-full bg-white/30">
                <div
                  className="h-1.5 rounded-full bg-white transition-all"
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Drop zone — when no image */}
      {!previewUrl && !uploading && (
        <div
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          onClick={() => !disabled && inputRef.current?.click()}
          className={cn(
            'flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed',
            'px-6 py-10 transition-colors cursor-pointer',
            disabled
              ? 'border-surface-200 bg-surface-50 cursor-not-allowed'
              : 'border-surface-300 hover:border-primary-400 hover:bg-primary-50',
          )}
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-100">
            <ImageIcon className="h-6 w-6 text-surface-400" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-surface-700">
              {disabled ? 'No image uploaded' : 'Drop image here or click to browse'}
            </p>
            <p className="text-xs text-surface-400 mt-1">
              JPEG, PNG, WebP · max {formatFileSize(MAX_IMAGE_SIZE_BYTES)}
            </p>
          </div>
          {!disabled && (
            <span className="flex items-center gap-1.5 text-xs font-medium text-primary-600">
              <Upload className="h-3.5 w-3.5" /> Choose image
            </span>
          )}
        </div>
      )}

      {/* Replace button */}
      {previewUrl && !uploading && !disabled && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700
                     font-medium transition-colors"
        >
          <Upload className="h-3.5 w-3.5" /> Replace image
        </button>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}

      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_ACCEPT_STRING}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
      />

      {/* Lightbox */}
      {lightbox && previewUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightbox(false)}
        >
          <img
            src={previewUrl}
            alt="Full size preview"
            className="max-h-full max-w-full rounded-xl object-contain shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={() => setLightbox(false)}
            className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center
                       rounded-full bg-white/10 text-white hover:bg-white/20 transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
}
